import * as React from "react";
import { useState, useEffect } from "react";
import { ArrowUp, Droplets, Thermometer, Wifi, Shield, Zap, Home, Download, Phone, Mail } from "lucide-react";
// Link removed - using LocalizedLink for all internal navigation
import ImageLightbox from "@/components/ui/image-lightbox";
import { ProductGallery } from "@/components/ui/product-gallery";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FeatureCardSimple } from "@/components/ui/feature-card-simple";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import Navigation from "@/components/Navigation";
import { SEO } from "@/components/SEO";
import { getProductByName } from "@/data/productReference";
import { ProductMetadata } from "@/components/ProductMetadata";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useI18n } from "@/i18n";

// Import images
import ariaMainImage from "@/assets/aria-series-product-main-opt.webp";
import ariaMainImageFallback from "@/assets/aria-series-product-main.jpg";
import ariaLuxuryLifestyle from "@/assets/aria-series-lifestyle-luxury.jpg";
import ariaOpenSeat from "@/assets/aria-series-open-seat.jpg";
import ariaInstallation from "@/assets/aria-series-installation.jpg";
import ariaModernLifestyle from "@/assets/aria-series-lifestyle-modern.jpg";

import { PriceFrom } from "@/components/products/PriceFrom";
const Aria = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { t } = useI18n();
  useScrollToTop(); // Scroll to top when page loads
  const product = getProductByName("ARIA");

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDownload = (filename: string) => {
    const link = document.createElement("a");
    link.href = `/files/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const keyFeatures = [
    "Wall-hung space-saving design",
    "Advanced bidet functionality with multiple wash modes",
    "Dual remote control system (wall-mounted and handheld)",
    "Instant water heating technology", 
    "Automatic seat opening and closing",
    "LED night lighting",
    "Self-cleaning nozzle system",
    "Adjustable water temperature and pressure",
    "Eco-friendly water-saving flush",
    "Premium ceramic construction",
    "Soft-close seat and lid",
    "Antibacterial coating"
  ];

  const specifications = [
    { label: "Model", value: "ARIA (Internal: H60)" },
    { label: "Installation Type", value: "Wall-hung" },
    { label: "Bowl Shape", value: "Elongated" },
    { label: "Dimensions (W×D×H)", value: "360 × 540 × 340 mm" },
    { label: "Weight", value: "32 kg" },
    { label: "Water Consumption", value: "3/4.5L dual flush" },
    { label: "Seat Material", value: "Antibacterial PP resin" },
    { label: "Power Requirements", value: "110-240V, 50/60Hz" },
    { label: "Water Pressure", value: "0.05-0.75 MPa" },
    { label: "Warranty", value: "3 years comprehensive" }
  ];

  const certifications = [
    "cUPC Certified",
    "ETL Listed", 
    "EPA WaterSense",
    "IAPMO Certified",
    "FCC Compliant",
    "CE Marking",
    "ISO 9001:2015"
  ];

  const productImages = [
    { src: ariaMainImage, alt: "ARIA H60 luxury smart toilet with elegant wall-hung design and intelligent automation", title: "Main Product View" },
    { src: ariaLuxuryLifestyle, alt: "ARIA H60 luxury smart toilet installed in sophisticated bathroom setting", title: "Luxury Installation" },
    { src: ariaOpenSeat, alt: "ARIA H60 luxury smart toilet advanced bidet system and self-cleaning nozzles", title: "Internal Components" },
    { src: ariaInstallation, alt: "ARIA H60 luxury smart toilet wall-hung installation frame system", title: "Installation System" },
    { src: ariaModernLifestyle, alt: "ARIA H60 luxury smart toilet in contemporary modern bathroom design", title: "Modern Installation" }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "ARIA H60 Smart Toilet",
    "description": "Experience the ARIA H60 smart toilet - architectural masterpiece combining elegant design with intelligent automation, gesture controls, and luxury bidet functions.",
    "brand": {
      "@type": "Brand",
      "name": "Aureum VII Trading",
      "logo": "https://www.aureum7trading.com/lovable-uploads/aureum-vii-trading-logo.png"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Aureum VII Trading",
      "url": "https://www.aureum7trading.com"
    },
    "image": [
      "https://www.aureum7trading.com/images/products/aria-series-product-main.jpg",
      "https://www.aureum7trading.com/images/products/aria-series-lifestyle-luxury.jpg",
      "https://www.aureum7trading.com/images/products/aria-series-open-seat.jpg"
    ],
    "sku": "ARIA-H60",
    "mpn": "H60",
    "gtin13": "8001234567893",
    "productID": "ARIA-H60",
    "category": "Smart Toilets > Luxury Bathroom Fixtures",
    "material": "Premium Ceramic, Antibacterial PP Resin",
    "color": "White",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "price": 600,
      "priceValidUntil": "2026-12-31",
      "url": "https://www.aureum7trading.com/products/aria",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Aureum VII Trading",
        "url": "https://www.aureum7trading.com"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 5,
            "maxValue": 14,
            "unitCode": "DAY"
          }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "@id": "https://www.aureum7trading.com/return-policy",
        "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn",
        "itemCondition": "https://schema.org/NewCondition",
        "refundType": "https://schema.org/FullRefund"
      }
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Dimensions",
        "value": "360 × 540 × 340 mm"
      },
      {
        "@type": "PropertyValue",
        "name": "Installation Type",
        "value": "Wall-hung"
      },
      {
        "@type": "PropertyValue",
        "name": "Water Consumption",
        "value": "3/4.5L dual flush"
      },
      {
        "@type": "PropertyValue",
        "name": "Power Requirements",
        "value": "110-240V, 50/60Hz"
      },
      {
        "@type": "PropertyValue",
        "name": "Weight",
        "value": "32kg"
      },
      {
        "@type": "PropertyValue",
        "name": "Warranty",
        "value": "3 years comprehensive"
      },
      {
        "@type": "PropertyValue",
        "name": "Certifications",
        "value": "CE, cUPC, ETL, WaterSense"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="ARIA H60 Wall-Hung Smart Toilet | Space-Saving Bidet"
        description="ARIA H60 wall-hung smart toilet: multi-function bidet, instant water heating, dual wireless controls and space-saving install for compact luxury bathrooms."
        keywords="smart toilet, bidet toilet, intelligent toilet, automated toilet, heated toilet seat, luxury smart toilet, premium bathroom fixtures, ARIA H60 wall-hung, architectural bathroom design, wall-mounted smart toilet, sanitary wellness technology, intelligent toilet UK, smart WC Germany"
        url="/products/aria"
        image="https://www.aureum7trading.com/images/products/aria-series-product-main.jpg"
        structuredData={productStructuredData}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "ARIA H60", url: "/products/aria" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge variant="secondary" className="text-sm">
                  Wall-Hung Collection
                </Badge>
                <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
                  {t.productPages.aria.heroTitle}
                  <span className="block text-2xl md:text-3xl text-muted-foreground font-medium mt-2">
                    {t.productPages.aria.heroSubtitle}
                  </span>
                </h1>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  {t.productPages.aria.heroDescription}
                </p>
              </div>

              <PriceFrom price={600} />

              {/* Features List */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span className="text-muted-foreground">{t.productPages.aria.features.bidetSpray}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span className="text-muted-foreground">{t.productPages.aria.features.waterSaving}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span className="text-muted-foreground">{t.productPages.aria.features.compactFit}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span className="text-muted-foreground">{t.productPages.aria.features.softClose}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <PDFDownloadButton productName="ARIA" />
                <Button variant="outline" asChild>
                  <LocalizedLink to="/smart-toilets">{t.productPages.aria.cta.backToCollections}</LocalizedLink>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-muted to-muted/50 p-8">
                <picture>
                  <source srcSet={ariaMainImage} type="image/webp" />
                  <img
                    src={ariaMainImageFallback}
                    alt="ARIA H60 compact wall-hung smart toilet with innovative space-saving design, premium bidet functions, eco-friendly water conservation technology, advanced hygiene features, and sleek minimalist white ceramic finish"
                    className="w-full h-full object-contain"
                    width="800"
                    height="800"
                    loading="eager"
                  />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Separator />

      {/* Product Gallery */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Product Gallery</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Explore the ARIA's elegant design and advanced features through detailed views and lifestyle installations.
            </p>
          </div>

          <ProductGallery images={productImages} className="max-w-4xl mx-auto" />
        </div>
      </section>

      <Separator />

      {/* Detailed Information Tabs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Tabs defaultValue="features" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="specifications">Specifications</TabsTrigger>
              <TabsTrigger value="installation">Installation</TabsTrigger>
              <TabsTrigger value="certifications">Certifications</TabsTrigger>
            </TabsList>

            <TabsContent value="features" className="mt-8 bg-card rounded-lg border shadow-sm p-6">
              <h3 className="text-2xl font-semibold mb-2 flex items-center gap-2">
                <Droplets className="h-5 w-5 text-primary" />
                Key Features
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Advanced features that make ARIA the perfect choice for modern bathrooms
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {keyFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="specifications" className="mt-8 bg-card rounded-lg border shadow-sm p-6">
              <h3 className="text-2xl font-semibold mb-2 flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Technical Specifications
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Detailed technical information and dimensions
              </p>
              <div className="grid gap-4">
                {specifications.map((spec, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0">
                    <span className="font-medium text-sm">{spec.label}</span>
                    <span className="text-sm text-muted-foreground">{spec.value}</span>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="installation" className="mt-8">
              <div className="bg-card rounded-lg border shadow-sm p-6">
                <h3 className="text-2xl font-semibold mb-2 flex items-center gap-2">
                  <Home className="h-5 w-5 text-primary" />
                  Installation Requirements
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Professional installation guidelines and requirements
                </p>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-3">Wall-Hung Installation</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Requires structural wall support (minimum 200kg load capacity)</li>
                      <li>• Installation frame must be secured to wall studs</li>
                      <li>• Access panel recommended for maintenance</li>
                      <li>• Minimum floor clearance: 400mm</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3">Plumbing Requirements</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Water supply: 15mm copper or flexible connector</li>
                      <li>• Waste outlet: 90-110mm diameter</li>
                      <li>• Water pressure: 0.05-0.75 MPa static pressure</li>
                      <li>• GFCI electrical outlet required within 1.5m</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Professional Installation</h4>
                    <p className="text-sm text-muted-foreground">
                      We strongly recommend professional installation by certified plumbers familiar with wall-hung toilet systems. 
                      Contact our support team for installer recommendations in your area.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="certifications" className="mt-8">
              <div className="bg-card rounded-lg border shadow-sm p-6">
                <h3 className="text-2xl font-semibold mb-2 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Certifications & Standards
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Industry certifications and compliance standards
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      <Shield className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm font-medium">{cert}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 bg-primary/5 rounded-lg">
                  <h4 className="font-semibold text-primary mb-2">Quality Assurance</h4>
                  <p className="text-sm text-muted-foreground">
                    All ARIA units undergo rigorous testing and quality control processes. 
                    Each toilet is individually tested for performance, safety, and durability 
                    before leaving our facility.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Separator />

      {/* Shipping & Delivery Information */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <ShippingTimeInfo variant="detailed" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <h2 className="text-3xl font-bold">Experience ARIA Excellence</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Transform your bathroom with the space-saving elegance and advanced hygiene technology of ARIA. 
              Contact us for personalized consultation and installation services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center bg-card rounded-lg border shadow-sm p-6">
              <Phone className="h-8 w-8 text-primary mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Expert Consultation</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Get personalized advice from our bathroom specialists
              </p>
              <Button variant="outline" className="w-full" asChild>
                <a href="tel:+34642278939">
                  Schedule Call
                </a>
              </Button>
            </div>

            <div className="text-center bg-card rounded-lg border shadow-sm p-6">
              <Download className="h-8 w-8 text-primary mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Technical Resources</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Download installation guides and specifications
              </p>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => handleDownload("RRL_Catalog.pdf")}
              >
                Download Specs
              </Button>
            </div>

            <div className="text-center bg-card rounded-lg border shadow-sm p-6">
              <Mail className="h-8 w-8 text-primary mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Custom Quote</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Get pricing for your specific installation needs
              </p>
              <Button asChild className="w-full bg-primary hover:bg-primary/90">
                <a href="mailto:info@aureum7trading.com?subject=ARIA%20H60%20Quote%20Request">
                  Request Quote
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <RelatedProducts currentProduct="ARIA" />

      {/* Footer */}
      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 rounded-full p-3"
          size="icon"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default Aria;